import React, { useState } from 'react';
import { useNotify } from 'react-admin';
import { Check, Eye, Loader2, Mail, MessageSquare, Phone } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { CustomerEvent, CustomerEventType, Partner, PhoneContact } from '../../types';
import { getToken } from '../../authentication/authProvider';
import { formatPhone } from '../../lib/phone';
import { fetchSignedDocuments, SignedDocuments } from './dispatchDocuments';
import { PartnerDocItem } from './partnerDocRequirements';
import { PartnerDocChip } from './PartnerDocsPanel';
import {
  buildEmailBody,
  buildEmailSubject,
  buildTransportSms,
  DispatchFacts,
  DispatchRole,
  emailHeading,
} from './notificationTemplates';

const API_BASE = (() => {
  const url = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';
  return url.endsWith('/') ? url : `${url}/`;
})();

const partnerPhones = (partner?: Partner | null): PhoneContact[] => {
  if (!partner) return [];
  if (partner.phones && partner.phones.length > 0) return partner.phones;
  if (partner.phone) return [{ label: 'Main', number: partner.phone }];
  return [];
};

export interface PartnerNotifyCardProps {
  step: number;
  role: DispatchRole;
  title: string;
  notified: boolean;
  partners: Partner[];
  isLoadingPartners?: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
  selectLabel: string;
  /** Renders a '' option (drop-off: "Same as crematorium"). */
  emptyOptionLabel?: string;
  /** Note shown instead of channel buttons while the '' option is selected. */
  emptyStateNote?: string;
  funeralId: string | number;
  facts: DispatchFacts;
  /** Crematorium only: offer the signed cremation agreement as an attachment. */
  showAgreementAttachment?: boolean;
  /** The documents this partner needs on this case (chips; §4 "what won't be included"). */
  docItems?: PartnerDocItem[];
  createCustomerEvent: (event: Partial<CustomerEvent>) => Promise<unknown>;
}

/**
 * One dispatch-notification recipient: partner picker, the channels their
 * contact info allows (Call / Text / Email), and an inline composer prefilled
 * with the role-specific template. Every send is logged as a customer event
 * with recipient_type = role so "Notified" state and the dispatch guard work.
 */
export const PartnerNotifyCard: React.FC<PartnerNotifyCardProps> = ({
  step,
  role,
  title,
  notified,
  partners,
  isLoadingPartners,
  selectedId,
  onSelect,
  selectLabel,
  emptyOptionLabel,
  emptyStateNote,
  funeralId,
  facts,
  showAgreementAttachment,
  docItems,
  createCustomerEvent,
}) => {
  const notify = useNotify();
  const partner = partners.find((p) => p.id?.toString() === selectedId) || null;
  const phones = partnerPhones(partner);

  const [openChannel, setOpenChannel] = useState<'text' | 'email' | null>(null);
  const [phoneIndex, setPhoneIndex] = useState(0);
  const [smsText, setSmsText] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [includeAgreement, setIncludeAgreement] = useState(true);
  const [docs, setDocs] = useState<SignedDocuments | null>(null);
  const [sending, setSending] = useState(false);

  const currentPhone = phones[Math.min(phoneIndex, phones.length - 1)] || null;

  const resetComposer = () => setOpenChannel(null);

  const handlePartnerChange = (id: string) => {
    onSelect(id);
    setPhoneIndex(0);
    resetComposer();
  };

  const handleCall = async () => {
    if (!partner || !currentPhone) return;
    try {
      setSending(true);
      await createCustomerEvent({
        type: CustomerEventType.PHONE_CALL,
        recipient_type: role,
        recipient_id: Number(partner.id),
        recipient_name: partner.name,
        contact_method: 'phone',
        contact_detail: currentPhone.number,
        message: `Called ${partner.name} at ${formatPhone(currentPhone.number)} (${currentPhone.label})`,
      });

      const token = await getToken();
      const response = await fetch(`${API_BASE}aircall/calls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phoneNumber: currentPhone.number, userId: '1533733' }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      notify(`Calling ${partner.name} at ${formatPhone(currentPhone.number)} (${currentPhone.label})`, {
        type: 'success',
      });
    } catch (error) {
      console.error('Error initiating phone call:', error);
      notify(
        `Error initiating phone call: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' },
      );
    } finally {
      setSending(false);
    }
  };

  const handleOpenText = () => {
    if (!partner) return;
    if (openChannel === 'text') {
      resetComposer();
      return;
    }
    setSmsText(buildTransportSms(facts, role));
    setOpenChannel('text');
  };

  const handleSendText = async () => {
    if (!partner || !currentPhone) return;
    try {
      setSending(true);
      await createCustomerEvent({
        type: CustomerEventType.TEXT_MESSAGE,
        recipient_type: role,
        recipient_id: Number(partner.id),
        recipient_name: partner.name,
        contact_method: 'sms',
        contact_detail: currentPhone.number,
        message: smsText,
      });

      const token = await getToken();
      const response = await fetch(`${API_BASE}aircall/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phoneNumber: currentPhone.number, message: smsText }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      notify(`Text sent to ${partner.name} at ${formatPhone(currentPhone.number)} (${currentPhone.label})`, {
        type: 'success',
      });
      resetComposer();
    } catch (error) {
      console.error('Error sending text message:', error);
      notify(
        `Error sending text: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' },
      );
    } finally {
      setSending(false);
    }
  };

  const handleOpenEmail = async () => {
    if (!partner) return;
    if (openChannel === 'email') {
      resetComposer();
      return;
    }
    const fetched = await fetchSignedDocuments(funeralId);
    setDocs(fetched);
    setIncludeAgreement(!!fetched.cremationContract);
    setEmailSubject(buildEmailSubject(role, facts));
    setEmailBody(
      buildEmailBody(role, facts, {
        hasAgreement: !!showAgreementAttachment && !!fetched.cremationContract,
        hasSp3: !!fetched.sp3File,
      }),
    );
    setOpenChannel('email');
  };

  const handleSendEmail = async () => {
    if (!partner?.email) return;
    try {
      setSending(true);

      // Refresh short-lived signed URLs right before sending.
      const fresh = await fetchSignedDocuments(funeralId);
      // Typed attachments + partnerId let the server record which documents
      // this partner received (case_partner_documents). Transport needs no
      // documents, so its request stays exactly as it was.
      const typed = role !== 'transport';
      const attachments: Array<{ url: string; kind?: string }> = [];
      if (showAgreementAttachment && includeAgreement && fresh.cremationContract) {
        attachments.push(
          typed ? { url: fresh.cremationContract, kind: 'cremation_agreement' } : { url: fresh.cremationContract },
        );
      }
      if (fresh.sp3File) {
        attachments.push(typed ? { url: fresh.sp3File, kind: 'sp3' } : { url: fresh.sp3File });
      }

      const emailAddresses = partner.email
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);

      const token = await getToken();
      const response = await fetch(`${API_BASE}funerals/${funeralId}/partner-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: emailAddresses,
          subject: emailSubject,
          body: emailBody,
          partnerName: partner.name,
          role,
          heading: emailHeading(role, facts),
          attachments,
          ...(typed
            ? {
                partnerId: String(partner.id),
                deceasedName: `${facts.deceasedFirstName} ${facts.deceasedLastName}`.trim(),
              }
            : {}),
          // T-099: the drop-off facility works the case in its portal, so its
          // dispatch email carries an "Open Cleo Portal" button. The dispatch
          // templates are English, so the button is too.
          ...(role === 'dropoff' ? { portalLink: true, language: 'en' } : {}),
        }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to send email');
      }

      await createCustomerEvent({
        type: CustomerEventType.EMAIL,
        recipient_type: role,
        recipient_id: Number(partner.id),
        recipient_name: partner.name,
        contact_method: 'email',
        contact_detail: emailAddresses.join(', '),
        subject: emailSubject,
        message: emailBody,
      });

      notify(`Email sent to ${partner.name}`, { type: 'success' });
      resetComposer();
    } catch (error) {
      console.error('Error sending email:', error);
      notify(
        `Error sending email: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' },
      );
    } finally {
      setSending(false);
    }
  };

  const hasPartner = !!partner;
  const canEmail = hasPartner && !!partner?.email;
  const canPhone = hasPartner && phones.length > 0;
  const showEmptyState = !selectedId && !!emptyOptionLabel;

  return (
    <Card className={cn(notified && 'border-green-600')}>
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white',
              notified ? 'bg-green-600' : 'bg-[#5e35b1]',
            )}
          >
            {notified ? <Check className="h-4 w-4" /> : step}
          </span>
          <CardTitle className="flex-1 text-base">{title}</CardTitle>
          {notified && <Badge variant="success">Notified</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        <div className="space-y-1.5">
          <Label htmlFor={`partner-select-${role}`}>{selectLabel}</Label>
          <select
            id={`partner-select-${role}`}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            value={selectedId}
            onChange={(e) => handlePartnerChange(e.target.value)}
            disabled={isLoadingPartners}
          >
            {emptyOptionLabel !== undefined ? (
              <option value="">{emptyOptionLabel}</option>
            ) : (
              <option value="" disabled>
                {isLoadingPartners ? 'Loading…' : 'Select…'}
              </option>
            )}
            {partners.map((p) => (
              <option key={p.id} value={p.id.toString()}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {!showEmptyState && docItems && docItems.length > 0 && (
          <div className="flex flex-wrap gap-1" title="Documents this partner needs on this case">
            {docItems.map((i) => (
              <PartnerDocChip key={i.documentType} item={i} />
            ))}
          </div>
        )}

        {showEmptyState ? (
          emptyStateNote && <p className="text-sm text-muted-foreground">{emptyStateNote}</p>
        ) : (
          <>
            {phones.length > 1 && (
              <div className="space-y-1.5">
                <Label htmlFor={`phone-select-${role}`}>Phone number</Label>
                <select
                  id={`phone-select-${role}`}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={phoneIndex}
                  onChange={(e) => setPhoneIndex(Number(e.target.value))}
                >
                  {phones.map((p, i) => (
                    <option key={`${p.number}-${i}`} value={i}>
                      {p.label}: {formatPhone(p.number)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {canPhone && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleCall()}
                    disabled={sending}
                  >
                    <Phone className="mr-1.5 h-4 w-4" /> Call
                  </Button>
                  <Button
                    variant={openChannel === 'text' ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={handleOpenText}
                    disabled={sending}
                  >
                    <MessageSquare className="mr-1.5 h-4 w-4" /> Text
                  </Button>
                </>
              )}
              <Button
                variant={openChannel === 'email' ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => void handleOpenEmail()}
                disabled={sending || !canEmail}
                title={!canEmail && hasPartner ? 'No email address on this partner' : undefined}
              >
                <Mail className="mr-1.5 h-4 w-4" /> Email
              </Button>
              {!canPhone && !canEmail && hasPartner && (
                <p className="self-center text-sm text-muted-foreground">
                  No phone or email on file for this partner.
                </p>
              )}
            </div>

            {openChannel === 'text' && (
              <div className="space-y-2 rounded-md bg-muted/50 p-3">
                <p className="text-sm text-muted-foreground">
                  Send text to {partner?.name}
                  {currentPhone ? ` at ${formatPhone(currentPhone.number)} (${currentPhone.label})` : ''}:
                </p>
                <Textarea
                  rows={5}
                  value={smsText}
                  onChange={(e) => setSmsText(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={resetComposer} disabled={sending}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={() => void handleSendText()} disabled={sending}>
                    {sending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Send
                  </Button>
                </div>
              </div>
            )}

            {openChannel === 'email' && (
              <div className="space-y-2 rounded-md bg-muted/50 p-3">
                <p className="text-sm text-muted-foreground">
                  Send email to {partner?.name} ({partner?.email})
                  {partner?.email?.includes(',') ? ' — multiple recipients' : ''}:
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor={`email-subject-${role}`}>Subject</Label>
                  <Input
                    id={`email-subject-${role}`}
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`email-body-${role}`}>Message</Label>
                  <Textarea
                    id={`email-body-${role}`}
                    rows={7}
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                  />
                </div>

                {/* Ontario has no SP3, so a non-crematorium ON email has nothing to attach */}
                {(showAgreementAttachment || facts.region !== 'ontario') && (
                <div className="space-y-2 rounded-md border bg-background p-3">
                  <p className="text-sm font-medium">Attachments</p>
                  {showAgreementAttachment && (
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#5e35b1]"
                          checked={!!docs?.cremationContract && includeAgreement}
                          disabled={!docs?.cremationContract}
                          onChange={(e) => setIncludeAgreement(e.target.checked)}
                        />
                        <span className={cn(!docs?.cremationContract && 'text-muted-foreground')}>
                          Cremation Agreement
                        </span>
                      </label>
                      {docs?.cremationContract ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (docs?.cremationContract) window.open(docs.cremationContract, '_blank');
                          }}
                        >
                          <Eye className="mr-1.5 h-4 w-4" /> Preview
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-amber-600">
                          Missing
                        </Badge>
                      )}
                    </div>
                  )}
                  {facts.region !== 'ontario' && (
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#5e35b1]"
                          checked={!!docs?.sp3File}
                          disabled
                        />
                        <span className={cn(!docs?.sp3File && 'text-muted-foreground')}>
                          SP3 Document
                        </span>
                      </label>
                      {docs?.sp3File ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (docs?.sp3File) window.open(docs.sp3File, '_blank');
                          }}
                        >
                          <Eye className="mr-1.5 h-4 w-4" /> Preview
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-amber-600">
                          Missing
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={resetComposer} disabled={sending}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={() => void handleSendEmail()} disabled={sending}>
                    {sending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Send
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
